import { useEffect, useRef, useState } from 'react'
import { API_BASE_URL, getToken } from '../api/httpClient'

/** Subscribes this browser to Web Push and keeps the subscription synced with
 *  the backend.  Returns the current permission state and a function to request
 *  permission + subscribe.  Calling subscribe() when already subscribed is a
 *  no-op.  Calling unsubscribe() deregisters from the backend and SW. */
export function usePushSubscription() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  )
  const [subscribed, setSubscribed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const subRef = useRef<PushSubscription | null>(null)

  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

  useEffect(() => {
    if (!supported) return
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        subRef.current = sub
        setSubscribed(!!sub)
      })
    })
  }, [supported])

  async function subscribe() {
    if (!supported) { setError('Push notifications are not supported in this browser'); return }
    try {
      setError(null)
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') return

      const vapidKey = await fetchVapidKey()
      if (!vapidKey) { setError('Push not configured on server'); return }

      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      })
      subRef.current = sub

      const json = sub.toJSON()
      await saveSubscription({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      })
      setSubscribed(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe')
    }
  }

  async function unsubscribe() {
    try {
      if (subRef.current) {
        const endpoint = encodeURIComponent(subRef.current.endpoint)
        const token = getToken()
        await fetch(`${API_BASE_URL}/me/push-subscription?endpoint=${endpoint}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        await subRef.current.unsubscribe()
        subRef.current = null
      }
      setSubscribed(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe')
    }
  }

  return { supported, permission, subscribed, error, subscribe, unsubscribe }
}

async function fetchVapidKey(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/push/vapid-public-key`)
    if (!res.ok) return null
    const data = (await res.json()) as { public_key?: string }
    return data.public_key ?? null
  } catch {
    return null
  }
}

async function saveSubscription(payload: { endpoint: string; p256dh: string; auth: string }) {
  const token = getToken()
  await fetch(`${API_BASE_URL}/me/push-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  })
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}
